"use client";

import { createContext, useContext, useMemo, type ReactNode } from "react";
import { preload } from "react-dom";

export interface LoadingSpinnerConfig {
  /** Image source URL for the logo displayed in the center of the spinner */
  logoSrc: string;
  /** Alt text for the logo image (defaults to "Logo" if not provided) */
  logoAlt?: string;
  /** Additional CSS classes for the logo image element */
  imgClassName?: string;
}

const LoadingSpinnerContext = createContext<LoadingSpinnerConfig | null>(null);

export interface LoadingSpinnerProviderProps {
  config: LoadingSpinnerConfig;
  children: ReactNode;
}

export function LoadingSpinnerProvider({
  config,
  children,
}: LoadingSpinnerProviderProps) {
  if (
    process.env.NODE_ENV === "development" &&
    typeof config.logoSrc !== "string"
  ) {
    console.warn(
      "LoadingSpinnerProvider: logoSrc must be a string URL, but received",
      typeof config.logoSrc,
    );
  }

  if (typeof config.logoSrc === "string") {
    preload(config.logoSrc, { as: "image" });
  }

  const value = useMemo(
    () => ({
      logoSrc: config.logoSrc,
      logoAlt: config.logoAlt,
      imgClassName: config.imgClassName,
    }),
    [config.logoSrc, config.logoAlt, config.imgClassName],
  );
  return (
    <LoadingSpinnerContext.Provider value={value}>
      {children}
    </LoadingSpinnerContext.Provider>
  );
}

/**
 * Returns the loading spinner config, or null if no provider is present.
 * Used internally by LoadingSpinner to read the default logo.
 */
export function useLoadingSpinnerConfig(): LoadingSpinnerConfig | null {
  return useContext(LoadingSpinnerContext);
}
